type CacheEntry<T> = {
  value: T;
  timestamp: number;
};

const createTimedLRUCache = <K, V>(options?: {
  timeout?: number;
  maxSize?: number;
}) => {
  const cache = new Map<K, CacheEntry<V>>();
  const { timeout = 5000, maxSize = 50 } = options || {};
  let clearTimer: ReturnType<typeof setTimeout> | null = null;

  const resetClearTimeout = () => {
    if (clearTimer !== null) {
      clearTimeout(clearTimer);
    }
    clearTimer = setTimeout(() => {
      cache.clear();
      clearTimer = null;
    }, timeout);
  };

  const get = (key: K): V | undefined => {
    const entry = cache.get(key);
    if (entry) {
      // Mise à jour pour le LRU
      cache.delete(key);
      cache.set(key, { value: entry.value, timestamp: Date.now() });
      resetClearTimeout();
      return entry.value;
    }
    return undefined;
  };

  const set = (key: K, value: V) => {
    if (cache.has(key)) {
      cache.delete(key); // pour repositionner en LRU
    }

    cache.set(key, { value, timestamp: Date.now() });

    if (cache.size > maxSize) {
      const oldestKey = cache.keys().next().value;
      cache.delete(oldestKey);
    }

    resetClearTimeout();
  };

  const getOrCompute = (key: K, computeFn: () => V): V => {
    const cached = get(key);
    if (cached !== undefined) {
      return cached;
    }

    const result = computeFn();
    set(key, result);
    return result;
  };

  const clear = () => {
    if (clearTimer) {
      clearTimeout(clearTimer);
    }
    cache.clear();
  };

  const deleteKey = (key: K) => {
    cache.delete(key);
  };

  return {
    get,
    set,
    getOrCompute,
    clear,
    delete: deleteKey,
  };
}

export default createTimedLRUCache;
